import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import type { Responsable } from '../../negocio/negocio.types';
import { ResponsablesService } from '../services/responsables.service';
import { estaAnulada, etiquetaVigencia } from '../utils/responsables';
import { mensajeDeError } from '../../negocio/errores';

interface Props {
  tienda: Pick<Responsable, 'cadena' | 'num' | 'tienda'>;
  onHide: () => void;
}

const instante = (v: string | null) =>
  v
    ? new Intl.DateTimeFormat('es-MX', {
        timeZone: 'America/Mexico_City',
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(v))
    : null;

/** Historial de supervisor y regional de UNA tienda (por cadena Y número: la 118 existe en TAF y en TRE). */
export const HistorialDialog: React.FC<Props> = ({ tienda, onHide }) => {
  const [filas, setFilas] = useState<Responsable[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setFilas(null);
    setError(null);
    ResponsablesService.historial(tienda.cadena, tienda.num)
      .then((h) => vivo && setFilas(h))
      .catch((e) => vivo && setError(mensajeDeError(e)));
    return () => {
      vivo = false;
    };
  }, [tienda.cadena, tienda.num]);

  return (
    <Dialog
      header={`Historial · ${tienda.cadena} ${tienda.num} ${tienda.tienda}`}
      visible
      onHide={onHide}
      style={{ width: '40rem' }}
      breakpoints={{ '640px': '95vw' }}
      dismissableMask
    >
      {error && <Message severity="error" text={error} className="w-full justify-start" />}
      {!filas && !error && (
        <div className="flex justify-center p-4">
          <ProgressSpinner style={{ width: '2rem', height: '2rem' }} />
        </div>
      )}
      {filas && filas.length === 0 && <p className="text-sm text-gray-500">Sin asignaciones registradas.</p>}
      {filas && filas.length > 0 && (
        <ol className="space-y-2 text-sm">
          {filas.map((f, i) => {
            const anulada = estaAnulada(f);
            return (
              <li
                key={f.id ?? `c-${i}`}
                className={`rounded-md border border-solid border-gray-200 p-3 ${anulada ? 'bg-gray-50 text-gray-400 line-through' : 'text-gray-700'}`}
              >
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="font-medium">{etiquetaVigencia(f)}</span>
                  <span className="text-xs text-gray-500">{f.origen === 'catalogo' ? 'Catálogo compilado' : f.asignadoPor}</span>
                </div>
                <div className="mt-1">
                  Supervisor: <b>{f.supervisor ?? '—'}</b> · Regional: <b>{f.regional ?? '—'}</b>
                </div>
                {(f.motivo || f.asignadoEn) && (
                  <div className="mt-1 text-xs text-gray-500">
                    {[instante(f.asignadoEn), f.motivo].filter(Boolean).join(' · ')}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </Dialog>
  );
};
